import React, { useState } from 'react';
import { Info } from 'lucide-react';

export interface MetricSummaryLine {
  metricKey: string;
  displayName: string;
  unit: string | null;
  previousValue: number | null;
  currentValue: number | null;
  change: number | null;
  changePercent: number | null;
  isNew: boolean;
  isImproving: boolean | null;
  direction: string;
  recentHistory: number[];
}

export interface MonthlyProgressSummary {
  reportMonth: string;
  fitnessScore: number;
  metrics: MetricSummaryLine[];
  streakMonths: number;
  totalReports: number;
  aiInsight?: string;
}

interface Props {
  summary: MonthlyProgressSummary;
  compact?: boolean;
  hideMetrics?: boolean;
  theme?: 'light' | 'dark';
}

function formatValue(value: number | null, unit: string | null, hideUnitSuffix = false): string {
  if (value === null || value === undefined) return '—';
  if (unit === 'seconds' || unit === 'time') {
    if (value >= 3600) {
      const h = Math.floor(value / 3600);
      const m = Math.floor((value % 3600) / 60);
      const s = Math.floor(value % 60);
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    const m = Math.floor(value / 60);
    const s = Math.floor(value % 60);
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  }
  if (hideUnitSuffix) return String(value);
  return unit ? `${value} ${unit}` : String(value);
}

function ScoreRing({ score, theme = 'dark' }: { score: number, theme?: 'light' | 'dark' }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 70 ? '#aad816' : score >= 45 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative flex items-center justify-center w-28 h-28">
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={radius} fill="none" stroke={theme === 'dark' ? "#27272a" : "#f3f4f6"} strokeWidth="8" />
        <circle
          cx="50" cy="50" r={radius} fill="none"
          stroke={color} strokeWidth="8"
          strokeDasharray={`${progress} ${circumference - progress}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
      </svg>
      <div className="text-center z-10">
        <div className={`text-3xl font-black ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{score}</div>
        <div className={`text-[10px] ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} uppercase font-bold tracking-wider`}>Score</div>
      </div>
    </div>
  );
}

const MonthlyProgressSummary: React.FC<Props> = ({ summary, compact = false, hideMetrics = false, theme = 'dark' }) => {
  const [showHelp, setShowHelp] = useState(false);
  const dateStr = summary.reportMonth.split('T')[0];
  const monthLabel = new Date(dateStr).toLocaleDateString('en-IN', {
    month: 'long', year: 'numeric'
  });

  const improvingCount = summary.metrics.filter(m => m.isImproving === true).length;
  const decliningCount = summary.metrics.filter(m => m.isImproving === false).length;

  return (
    <div className={`relative z-10 ${theme === 'dark' ? 'glass-card' : 'bg-white rounded-3xl border border-gray-100 shadow-sm'}`}>
      {/* Header */}
      <div className={`px-5 py-4 border-b ${theme === 'dark' ? 'border-zinc-800' : 'border-gray-50'} flex items-center justify-between`}>
        <div>
          <p className={`text-[10px] ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} uppercase font-black tracking-widest`}>Monthly Progress</p>
          <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} font-black text-lg uppercase italic`}>{monthLabel}</h3>
        </div>
        {!compact && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-[#aad816] font-bold">🔥 {summary.streakMonths}mo streak</span>
          </div>
        )}
      </div>

      <div className={`p-5 ${compact ? 'space-y-4' : hideMetrics ? 'flex items-center justify-center' : 'grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6'}`}>
        {/* Fitness Score */}
        {!compact && (
          <div className="flex flex-col items-center justify-center gap-2 group relative">
            <div className="flex flex-col items-center justify-center gap-2">
              <ScoreRing score={summary.fitnessScore} theme={theme} />
              {!hideMetrics && (
                <div className="flex gap-3 text-xs">
                  <span className="text-[#aad816] font-bold">↑ {improvingCount}</span>
                  <span className="text-red-400 font-bold">↓ {decliningCount}</span>
                </div>
              )}
            </div>
            
            {/* Help Indicator */}
            <button 
              className={`p-1.5 rounded-full hover:bg-zinc-100 transition-colors mt-2 ${theme === 'dark' ? 'text-zinc-500 hover:bg-zinc-800' : 'text-gray-300'}`}
              onMouseEnter={() => setShowHelp(true)}
              onMouseLeave={() => setShowHelp(false)}
              onClick={() => setShowHelp(!showHelp)}
            >
              <Info size={14} />
            </button>

            {/* Help Tooltip/Note */}
            {showHelp && (
              <div className={`absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-64 p-4 rounded-2xl shadow-2xl z-[100] animate-in fade-in slide-in-from-bottom-2 duration-200 border ${
                theme === 'dark' ? 'bg-zinc-900 border-zinc-800 text-zinc-300' : 'bg-white border-gray-100 text-gray-600'
              }`}>
                <h4 className={`text-[10px] font-black uppercase tracking-widest mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Score Logic</h4>
                <div className="space-y-3">
                  <p className="text-[10px] leading-relaxed font-medium">
                    Your **Fitness Score** reflects the net trend of all tracked metrics. Improving a marker adds points, while a decline subtracts.
                  </p>
                  <div className={`pt-2 border-t ${theme === 'dark' ? 'border-zinc-800' : 'border-gray-50'}`}>
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${theme === 'dark' ? 'text-lime-500' : 'text-lime-600'}`}>How to Improve</p>
                    <ul className="text-[10px] space-y-1 font-medium list-disc list-inside">
                      <li>Focus on consistent small gains</li>
                      <li>Track metrics in every category</li>
                      <li>Maintain a streak to boost recovery score</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metrics List */}
        {!hideMetrics && (
          <div className="space-y-2">
            {compact && (
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} uppercase font-bold tracking-wider`}>Fitness Score</span>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-black ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{summary.fitnessScore}</span>
                  <span className={`text-[10px] ${theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'}`}>/100</span>
                </div>
              </div>
            )}

            {summary.metrics.slice(0, compact ? 4 : undefined).map(m => {
              const sign = m.change !== null && m.change > 0 ? '+' : '';
              const improving = m.isImproving;
              const isSame = m.change === 0 && !m.isNew;
              const color = improving === true
                ? 'text-[#aad816]'
                : isSame
                ? 'text-orange-400'
                : improving === false
                ? 'text-red-400'
                : theme === 'dark' ? 'text-zinc-500' : 'text-gray-400';

              return (
                <div
                  key={m.metricKey}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between py-3 sm:py-2 border-b ${theme === 'dark' ? 'border-zinc-800/50' : 'border-gray-50'} last:border-0 gap-2 sm:gap-0`}
                >
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                    <span className={`text-base sm:text-lg shrink-0 transition-transform ${color}`}>
                      {improving === true ? '↑' : improving === false ? '↓' : isSame ? '•' : ''}
                    </span>
                    <span className={`text-[13px] sm:text-sm ${theme === 'dark' ? 'text-zinc-300' : 'text-gray-600'} font-medium truncate`}>{m.displayName}</span>
                    {m.isNew && (
                      <span className="text-[8px] bg-[#aad816]/20 text-[#aad816] px-1.2 py-0.4 rounded-full font-black uppercase shrink-0">NEW</span>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-1.5 sm:gap-3 shrink-0 sm:ml-auto flex-wrap max-w-full">
                    {m.recentHistory.map((val, idx) => {
                      const isLast = idx === m.recentHistory.length - 1;
                      
                      let valColor = '';
                      if (isLast) {
                        valColor = color;
                      } else {
                        valColor = theme === 'dark' ? 'text-zinc-400' : 'text-gray-500';
                      }

                      return (
                        <React.Fragment key={idx}>
                          {idx > 0 && <span className={`text-[12px] ${theme === 'dark' ? 'text-[#AAD816]/30' : 'text-gray-200'} font-black px-0.5`}>›</span>}
                          <span className={`text-xs sm:text-sm font-bold ${valColor} transition-all ${isLast ? 'scale-110' : ''}`}>
                            {formatValue(val, m.unit, !isLast)}
                          </span>
                        </React.Fragment>
                      );
                    })}
                    {m.change !== null && m.change !== 0 && (
                      <span className={`text-[9px] sm:text-[11px] font-black uppercase ml-1 sm:ml-2 ${color} shrink-0`}>
                        ({sign}{m.changePercent}%)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {compact && summary.metrics.length > 4 && (
              <p className={`text-xs ${theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'} text-center pt-1`}>
                +{summary.metrics.length - 4} more metrics
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyProgressSummary;


